import type { ColunaDeLotes, SentidoDeOrdenacao, SituacaoDoLote } from "@plantus/api-client";
import type { OrdemDaTabela, SentidoDaOrdem } from "@plantus/ui";

import { ROTULO_DA_SITUACAO } from "./situacaoDoLote";

/**
 * A ordem e o filtro da lista de lotes — <b>a tradução entre o gesto e o
 * vocabulário fechado do servidor</b>.
 *
 * <h2>Por que isto é um arquivo, e não três linhas dentro da tela</h2>
 *
 * <p>Porque é a única parte desta tela que dá para provar sem navegador. O
 * resto — a linha fina, o cartão no celular, quantos lotes cabem na dobra — se
 * mede em Chrome; isto se testa. E o que se testa aqui é o que <b>não pode</b>
 * dar errado: que o nome de coluna que sai daqui exista no enum do servidor.
 *
 * <h2>⚠️ A coluna nunca viaja como texto livre</h2>
 *
 * <p>`order by` do SQL não aceita parâmetro ligado. Quem monta a cláusula com o
 * que o navegador mandou concatena texto do cliente dentro do SQL, e é por aí
 * que se entra. O servidor recusa valor desconhecido; este mapa garante que o
 * cliente nem chegue a mandar um — a `chave` da coluna da tabela é do
 * componente visual e não tem por que casar com o nome do campo lá.
 *
 * <h2>Por que a coluna de RECEBIMENTO não está entre as ordenáveis</h2>
 *
 * <p>Ela não é uma coluna desta tabela: a lista mostra validade, não a data de
 * entrada. Mas <b>ela é a ordem padrão</b> (`recebidoEm desc` — R4), e por isso
 * o ciclo de três estados abaixo não é enfeite: sem o terceiro, "o mais recente
 * em cima" só voltaria recarregando a página.
 */

/**
 * A `chave` de cada coluna ordenável da tabela → a coluna do servidor.
 *
 * <p>São quatro dos cinco valores de {@link ColunaDeLotes}. O quinto,
 * `RECEBIMENTO`, é a ordem padrão e se alcança <b>largando</b> a ordenação.
 */
export const ORDEM = {
  codigo: "CODIGO",
  quantidade: "QUANTIDADE",
  validade: "VALIDADE",
  situacao: "SITUACAO",
} as const satisfies Record<string, ColunaDeLotes>;

/** As chaves de coluna que a tabela pode ordenar. */
export type ChaveOrdenavel = keyof typeof ORDEM;

/**
 * A ordem padrão é a <b>ausência</b> de ordem pedida.
 *
 * <p>Não é `RECEBIMENTO`+`DESC` escrito aqui, e a diferença importa: escrito
 * aqui, ele seria uma segunda cópia da regra R4 — e no dia em que o servidor
 * mudasse a ordem padrão, a tela continuaria pedindo a antiga em silêncio. Sem
 * parâmetro, quem responde é quem serve a lista.
 */
export const ORDEM_PADRAO: OrdemDaTabela | null = null;

/**
 * O ciclo do clique no cabeçalho: <b>crescente → decrescente → padrão</b>.
 *
 * <p>Três estados, e o terceiro é o que justifica os outros dois. Com dois
 * estados só, quem clicasse em "Validade" para conferir o que vence primeiro
 * ficaria preso: não haveria gesto para voltar ao "mais recente em cima" que a
 * lista sempre teve, e a pessoa recarregaria a página — que é o que se faz
 * quando a interface não oferece a volta.
 *
 * <p>Coluna diferente da atual sempre começa em <b>crescente</b>. Herdar o
 * sentido da coluna anterior faria a mesma sequência de cliques produzir ordens
 * diferentes conforme o que se clicou antes.
 */
export function alternar(
  atual: OrdemDaTabela | null,
  chave: string,
): OrdemDaTabela | null {
  if (atual === null || atual.chave !== chave) return { chave, sentido: "crescente" };
  if (atual.sentido === "crescente") return { chave, sentido: "decrescente" };
  return ORDEM_PADRAO;
}

/** `crescente` → `ASC`. O vocabulário do domínio é português; o da rota, não. */
export function sentidoDaApi(sentido: SentidoDaOrdem): SentidoDeOrdenacao {
  return sentido === "crescente" ? "ASC" : "DESC";
}

/**
 * O que a ordem escolhida vira na consulta — ou <b>nada</b>, quando é a padrão.
 *
 * <p>Chave fora do mapa devolve `{}`, e não uma consulta com coluna inventada:
 * o servidor recusaria, e a lista sumiria com um erro que ninguém entenderia
 * olhando. O caminho de uma coluna nova é acrescentá-la ao mapa.
 */
export function ordemNaConsulta(ordem: OrdemDaTabela | null): {
  ordenarPor?: ColunaDeLotes;
  sentido?: SentidoDeOrdenacao;
} {
  if (ordem === null) return {};
  const coluna = (ORDEM as Record<string, ColunaDeLotes | undefined>)[ordem.chave];
  if (coluna === undefined) return {};
  return { ordenarPor: coluna, sentido: sentidoDaApi(ordem.sentido) };
}

/**
 * As situações oferecidas no filtro, <b>na ordem do ciclo de vida do material</b>
 * — não em ordem alfabética nem na do enum.
 *
 * <p>⚠️ Hoje só `QUARENTENA` é alcançável: não existe `aprovar()`, e essa
 * ausência é o controle. As outras quatro aparecem no filtro assim mesmo, e é
 * decisão: um filtro que só oferece o estado em que tudo está não ensina nada
 * sobre o que a tela vai fazer quando a liberação existir — e um filtro que
 * cresce sozinho no dia da entrega é um filtro que ninguém revisou.
 *
 * <p>Filtrar por uma situação que não tem material devolve <b>lista vazia</b>,
 * que é a resposta certa e verificável: "não há material aprovado" é diferente
 * de "esta tela não sabe responder".
 */
export const SITUACOES_DO_FILTRO: readonly {
  id: SituacaoDoLote;
  rotulo: string;
}[] = (
  ["QUARENTENA", "APROVADO", "REPROVADO", "BLOQUEADO", "CONSUMIDO"] as const
).map((id) => ({ id, rotulo: ROTULO_DA_SITUACAO[id] }));

/**
 * O período está de trás para a frente?
 *
 * <p>Conferido <b>aqui também</b>, e não só no servidor. O servidor recusa — é
 * ele quem manda —, mas a recusa dele chega como falha da consulta, e a tela
 * mostraria "não foi possível carregar os lotes" para alguém que apenas digitou
 * as datas fora de ordem. Lista vazia seria pior ainda: leria-se como "não há
 * material neste intervalo".
 *
 * <p>Data pela metade (`2026-08` enquanto se digita) não é período invertido:
 * comparar texto ISO só vale com os dois lados completos.
 */
export function periodoInvertido(de: string, ate: string): boolean {
  if (de.length !== 10 || ate.length !== 10) return false;
  return de > ate;
}

/** Nenhum filtro pedido — o que a tela mostra ao abrir, e o alvo de "Limpar". */
export interface FiltroDeLotes {
  termo: string;
  situacoes: readonly SituacaoDoLote[];
  de: string;
  ate: string;
}

export const FILTRO_VAZIO: FiltroDeLotes = {
  termo: "",
  situacoes: [],
  de: "",
  ate: "",
};

/**
 * <b>Quantos</b> recortes estão em vigor.
 *
 * <p>É contagem, e não sim/não, porque a faixa de filtros fica <b>fechada</b>
 * por decisão de densidade — aberta ela custava 252px antes do primeiro lote.
 * O número no rótulo do gatilho é o que impede o caso perigoso de uma faixa
 * fechada: uma lista recortada que se parece com a lista inteira. Sem ele, a
 * pessoa conferiria "não há material vencido" sobre uma lista filtrada por um
 * período que ela mesma escolheu meia hora antes.
 *
 * <p>O período conta como <b>um</b>: "de 01 a 31" é um recorte, não dois.
 *
 * <h2>🔴 A ORDEM NÃO ENTRA NESTA CONTA — ordenar não recorta</h2>
 *
 * <p>Ela entrava, e o efeito é o defeito que esta função existe para impedir,
 * virado do avesso. Um clique em "Validade", <b>sem filtro nenhum</b>,
 * produzia <i>"12 lote(s) · lista recortada por 1 filtro(s)"</i> na legenda e
 * <i>"Filtros · 1"</i> no gatilho — sobre a lista <b>inteira</b>, com os doze
 * lotes na tela.
 *
 * <p>Numa tela de conferência regulada, <b>afirmar um recorte que não houve é
 * tão ruim quanto esconder um que houve</b>: quem lê "recortada" numa lista de
 * material para de tirar conclusões dela, vai procurar o filtro que não existe
 * e não acha — e no melhor caso perde a confiança na legenda, que é
 * exatamente a peça que precisa ser acreditada no dia em que houver recorte de
 * verdade. Uma tela que grita quando não é nada ensina a ignorar o grito.
 *
 * <p>A ordem continua sendo dita — <b>como ordem</b>, em {@link
 * ordemEmPalavras}, no mesmo lugar em que o total se lê.
 */
export function contarFiltros(filtro: FiltroDeLotes): number {
  return (
    (filtro.termo !== "" ? 1 : 0)
    + (filtro.situacoes.length > 0 ? 1 : 0)
    + (filtro.de !== "" || filtro.ate !== "" ? 1 : 0)
  );
}

/**
 * Há algum recorte em vigor? É o que decide se a lista na tela é <b>menor</b>
 * que a lista do servidor — e, com isso, qual das duas frases a lista vazia diz.
 *
 * <p>⚠️ Não pergunte isto para decidir se "Limpar" aparece: ordenar não
 * recorta, mas <b>é</b> algo a limpar. Para aquilo há {@link haOQueLimpar}.
 */
export function temFiltro(filtro: FiltroDeLotes): boolean {
  return contarFiltros(filtro) > 0;
}

/**
 * Há algo que "Limpar" desfaça — recorte <b>ou</b> ordem.
 *
 * <p>São duas perguntas e não uma, e foi tratá-las como uma que produziu a
 * legenda mentirosa. Esta é a pergunta do <b>botão</b>: nenhuma coluna oferece
 * o caminho de volta a `recebidoEm desc` num clique só, então, se a ordem não
 * contasse aqui, o "Limpar" sumiria com a lista ainda ordenada por outra coisa
 * e sem gesto nenhum para voltar.
 *
 * <p>A outra pergunta — "a lista está recortada?" — é {@link temFiltro}, e
 * responde <b>não</b> quando só houve ordenação.
 */
export function haOQueLimpar(filtro: FiltroDeLotes, ordem: OrdemDaTabela | null): boolean {
  return temFiltro(filtro) || ordem !== null;
}

/**
 * A ordem em vigor, <b>dita como ordem</b> — ou `null` quando é a padrão.
 *
 * <p>Ela precisava de um lugar depois de sair da contagem de filtros, e este é
 * o certo: a legenda da tabela, onde o total já se lê. Quem confere uma lista
 * de material precisa saber por onde ela está ordenada tanto quanto precisa
 * saber se ela está recortada — "o que vence primeiro" é uma pergunta que se
 * responde com a ordem, não com um filtro.
 *
 * <p>🔴 E ela é a única forma de o <b>papel</b> dizer isso. Na tela a ordem é
 * desenhada por uma seta de 8px no cabeçalho; no papel a seta não vai (ver
 * `ui.css`, seção da Tabela), porque `↕` oferece um gesto que a folha não tem
 * e `↑` afirma um sentido com um glifo que se lê errado de longe. Frase se lê;
 * glifo se interpreta.
 *
 * <p>O rótulo vem de <b>quem chama</b>, e de propósito: ele é o mesmo texto
 * escrito na coluna da tabela. Uma segunda cópia dele aqui divergiria no dia
 * em que alguém renomeasse a coluna — e a legenda passaria a nomear uma coluna
 * que a tabela não tem mais.
 */
export function ordemEmPalavras(
  ordem: OrdemDaTabela | null,
  rotuloDaColuna: (chave: string) => string | undefined,
): string | null {
  if (ordem === null) return null;
  const rotulo = rotuloDaColuna(ordem.chave);
  // Coluna desconhecida não vira "ordenada por undefined": a consulta também
  // ignora chave fora do mapa (ver `ordemNaConsulta`), então a lista está na
  // ordem padrão e a legenda não tem o que afirmar.
  if (rotulo === undefined) return null;
  return `ordenada por ${rotulo}, ${ordem.sentido}`;
}
